import React from 'react'
import { Layout } from '../../components/Layout'
import { H1, H3, H4 } from '@blueprintjs/core'
import { Callout } from '../../components/ui/callout'
import { Card } from '../../components/ui/card'
import { SyntaxHighlightedCode } from '../../components/SyntaxHighlightedCode'
// Import the CSS for CodeMirror and our custom theme
import 'codemirror/lib/codemirror.css'
import '../../components/actions2/merchjar-theme.scss'
import { subDays } from 'date-fns'
import { useUser } from '../../hooks/useUser'
import {
    CUSTOM_FIELD_DATA_TYPES,
    useCustomFieldCatalog,
    type CustomFieldDefinition,
} from '../../features/custom-fields'

export const customFieldQuickstartExamples = (
    definitions: readonly CustomFieldDefinition[],
): { number: string; boolean: string; string: string; missing: string } => {
    const key = (dataType: CustomFieldDefinition['data_type'], fallback: string) =>
        definitions.find((definition) => definition.data_type === dataType)?.key ?? fallback
    const numberKey = key(CUSTOM_FIELD_DATA_TYPES.Number, 'cf_your_number_field')
    const booleanKey = key(CUSTOM_FIELD_DATA_TYPES.Boolean, 'cf_your_boolean_field')
    const stringKey = key(CUSTOM_FIELD_DATA_TYPES.String, 'cf_your_string_field')
    return {
        number: `custom.${numberKey} >= 0.25`,
        boolean: `custom.${booleanKey} = true`,
        string: `custom.${stringKey} = "priority"`,
        missing: `is_null(custom.${numberKey}) = true`,
    }
}

export const RecipesV2Quickstart = (): JSX.Element => {
    const { activeProfile } = useUser()
    const profileId = activeProfile ? String(activeProfile.profile_id) : null
    const { catalog: customFieldCatalog, editorContext: customFieldContext } =
        useCustomFieldCatalog(profileId, 'target')
    const customFieldExamples = customFieldQuickstartExamples(customFieldCatalog?.definitions ?? [])
    const breadcrumbs = [
        { href: '/', text: 'Home' },
        { href: '/recipes', text: 'Recipes' },
        { text: 'Recipes V2 Quickstart' },
    ]

    const today = new Date()

    return (
        <Layout breadcrumbs={breadcrumbs}>
            <div className="max-w-4xl mx-auto">
                <H1 className="mb-6">Recipes V2 Quickstart Guide</H1>

                <Callout intent="primary" icon="info-sign" className="mb-6">
                    <p className="text-lg">
                        Recipes V2 is our next-generation automation system that provides more
                        powerful and flexible ways to automate your Amazon advertising with an
                        improved syntax and enhanced capabilities.
                    </p>
                </Callout>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">What's New in Recipes V2?</H3>
                    The new syntax allows for more powerful and flexible queries.
                    <ul className="mt-2 list-disc pl-6">
                        <li>Compare different time periods (like this month vs. last month)</li>
                        <li>
                            Perform math with your metrics (like finding items where sales are
                            dropping)
                        </li>
                        <li>Declare and use variables</li>
                        <li>If statements</li>
                    </ul>
                    <SyntaxHighlightedCode
                        code={`// Find products where sales have dropped significantly\n// (current month is 10% lower than average of last 3 months)\nsales(30d) < sales(90d) / 3 * 0.9`}
                        version={2}
                        className="mt-4"
                    />
                    <SyntaxHighlightedCode
                        code={`// Find products where sales have dropped significantly\n// (current month is 10% lower than average of last 3 months)\nlet avg_monthly_sales = sales(90d) / 3;\nclicks(30d) < avg_monthly_sales * 0.9`}
                        version={2}
                        className="mt-4"
                    />
                    <SyntaxHighlightedCode
                        code={`// Find ads where clicks have dropped by more than 10%\n// compared to the previous month\nclicks(30d) < clicks(31d..61d) * 0.9`}
                        version={2}
                        className="mt-4"
                    />
                    <SyntaxHighlightedCode
                        code={`let high_volume = case(\n    when orders(30d) > 20 then 1,\n    else 0\n);\n\nlet acos_bracket = case(\n    when acos(30d) > 50% then "high",\n    when acos(30d) > 30% then "medium",\n    else "low"\n);\n\n(acos_bracket = "high" or acos_bracket = "medium") and high_volume = 1`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Overview</H3>
                    <p>
                        We've updated the recipe syntax to make it more powerful and easier to use.
                        While it might look similar to what you're used to, it can now do much more
                        to help you find exactly what you need in your campaigns.
                    </p>
                    <p className="mt-1">
                        If you're familiar with our previous version, you'll feel right at home -
                        we've kept many things similar while adding helpful new features.
                    </p>

                    <H4 className="mt-6">New date range selections</H4>
                    <p>
                        We've made it easier to specify when you want to look at your data. Instead
                        of writing out "over the last X days", you can now use simple shortcuts.
                    </p>
                    <p className="mt-1">Here's a basic example:</p>

                    <SyntaxHighlightedCode
                        code={`// Old way\nover the last 30 days clicks > 10`}
                        version={1}
                        className="mt-4"
                    />

                    <SyntaxHighlightedCode
                        code={`// New way\nclicks(30d) > 10`}
                        version={2}
                        className="mt-4"
                    />

                    <p>
                        When you look at multiple metrics (like clicks and sales), you'll need to
                        specify the time period for each one:
                    </p>

                    <SyntaxHighlightedCode
                        code={`// Looking at clicks, impressions, and sales for the last 30 days\nclicks(30d) > 10 and impressions(30d) > 100 and sales(30d) = 0`}
                        version={2}
                        className="mt-4"
                    />

                    <p>
                        This new approach makes it much clearer when you're looking at different
                        time periods:
                    </p>

                    <SyntaxHighlightedCode
                        code={`clicks(30d) > 10
    and acos(30d) > 50%
    and sales(90d) > $10
    and sales(lifetime) > $100`}
                        version={2}
                        className="mt-4"
                    />

                    <p>compared to the old way:</p>

                    <SyntaxHighlightedCode
                        code={`// V1 Syntax
(over the last 30 days clicks > 10 and acos > 50%)
    and over the last 90 days sales > $10
    and over the lifetime sales > $100`}
                        version={1}
                        className="mt-4"
                    />

                    <ul className="mt-2 list-disc pl-6">
                        <li>Compare different time periods (like this month vs. last month)</li>
                        <li>
                            Perform math with your metrics (like finding items where sales are
                            dropping)
                        </li>
                    </ul>
                    <SyntaxHighlightedCode
                        code={`// Find products where sales have dropped significantly\n// (current month is 10% lower than average of last 3 months)\nsales(30d) < sales(90d) / 3 * 0.9`}
                        version={2}
                        className="mt-4"
                    />
                    <SyntaxHighlightedCode
                        code={`// Find ads where clicks have dropped by more than 10%\n// compared to the previous month\nclicks(30d) < clicks(31d..61d) * 0.9`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Metrics & Properties</H3>
                    <p>
                        We've made a clearer divide between what is a metric and what is a property
                        in order to make the syntax more intuitive.
                    </p>
                    <p className="mt-1">
                        In the v1 syntax, you could have a recipe like this:
                        <SyntaxHighlightedCode
                            code={`over the last 30 days bid > $0.10`}
                            version={1}
                            className="mt-4"
                        />
                        Intuitively, this looks like it would be looking at the historical bid data.
                        But it was not, it was only ever looking at the current bid data.
                    </p>
                    <p className="mt-1">
                        To clear this up, in the v2 syntax, metrics require a time period, while
                        properties are not allowed a time period.
                    </p>

                    <H4 className="mt-6">Metrics (Performance Data)</H4>
                    <p>
                        Metrics are numbers that change over time and are tied to a specific time
                        period, such as
                    </p>
                    <ul className="list-disc pl-6">
                        <li>clicks</li>
                        <li>sales</li>
                        <li>orders</li>
                        <li>impresions</li>
                        <li>ACoS</li>
                    </ul>
                    <p className="mt-4">Metrics always need a time period.</p>

                    <SyntaxHighlightedCode
                        code={`clicks(30d) // ✓ Correct: Looking at clicks over 30 days\nclicks      // ✗ Incorrect: Looking at current clicks`}
                        version={2}
                        className="mt-4"
                    />

                    <H4 className="mt-6">Properties</H4>

                    <p>These are current settings or details about your campaigns, like:</p>
                    <ul className="list-disc pl-6">
                        <li>Campaign name</li>
                        <li>Budget</li>
                        <li>Bid</li>
                        <li>Ad group name</li>
                    </ul>
                    <p className="mt-1">Properties don't use time periods:</p>

                    <SyntaxHighlightedCode
                        code={`bid > $0.25          // ✓ Correct: Checking current bid\nbid(30d) > $0.25     // ✗ Wrong: Bids don't use time periods`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Profile custom fields</H3>
                    <p>
                        Custom fields are typed properties defined for one entity keyspace. These
                        examples use your Keywords &amp; targets catalog when matching fields exist.
                    </p>
                    <p className="mt-2">
                        Start typing a field's display name in the recipe editor and choose it from
                        autocomplete. The editor inserts its immutable{' '}
                        <code>custom.&lt;key&gt;</code> reference; renaming the display name does
                        not change that key.
                    </p>
                    <SyntaxHighlightedCode
                        code={`// Number\n${customFieldExamples.number}\n\n// Boolean\n${customFieldExamples.boolean}\n\n// String\n${customFieldExamples.string}`}
                        version={2}
                        customFieldContext={customFieldContext}
                        className="mt-4"
                    />
                    <p className="mt-3">
                        An entity with no value set has a missing value, represented as SQL null.
                        Test that state with <code>is_null(...)</code>; it is different from false,
                        zero, or an empty string (empty strings cannot be saved).
                    </p>
                    <SyntaxHighlightedCode
                        code={`// Find rows where this custom field is not set\n${customFieldExamples.missing}`}
                        version={2}
                        customFieldContext={customFieldContext}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Enhanced Time Periods</H3>

                    <p>You have three flexible ways to look at your data:</p>

                    <H4 className="mt-6">1. Relative Time Periods</H4>
                    <p>
                        Look back a certain number of days. Today is {today.toLocaleDateString()}.
                    </p>

                    <SyntaxHighlightedCode
                        code={`clicks(30d)    // Last 30 days (from ${subDays(
                            today,
                            30,
                        ).toLocaleDateString()} to ${today.toLocaleDateString()})\nclicks(90d)    // Last 90 days (from ${subDays(
                            today,
                            90,
                        ).toLocaleDateString()} to ${today.toLocaleDateString()})`}
                        version={2}
                        className="mt-4"
                    />

                    <p className="mt-1">
                        This is an inclusive time period, including both the start and end dates.
                    </p>

                    <H4 className="mt-6">2. Literal Date Ranges</H4>
                    <p>Look at exact dates:</p>

                    <SyntaxHighlightedCode
                        code={`clicks(2024-01-01..2024-01-31)    // All of January 2024`}
                        version={2}
                        className="mt-4"
                    />

                    <p className="mt-1">
                        This is an inclusive time period, including both the start and end dates.
                    </p>

                    <p className="mt-1">
                        Make sure to use the format:{' '}
                        <code className="bg-gray-100 p-1 rounded">YYYY-MM-DD</code>
                    </p>

                    <H4 className="mt-6">3. Relative Offset Time Periods</H4>
                    <p>Look at earlier time periods to compare performance:</p>

                    <SyntaxHighlightedCode
                        code={`clicks(30d..60d) // from 30 days ago to 60 days ago (${subDays(
                            today,
                            30,
                        ).toLocaleDateString()} to ${subDays(today, 60).toLocaleDateString()})`}
                        version={2}
                        className="mt-4"
                    />

                    <p className="mt-1">
                        Pro Tip: Use these time periods to spot trends in your data. For example,
                        you can see if performance is improving or declining over time.
                    </p>

                    <p className="mt-1">
                        Either the leading or the trailing time period can be omitted, allowing you
                        to look at the entire time period or just the last X days.
                    </p>

                    <SyntaxHighlightedCode
                        code={`clicks(..60d) // from 60 days ago to today (${subDays(
                            today,
                            60,
                        ).toLocaleDateString()} to ${today.toLocaleDateString()})\nclicks(30d..) // lifetime, except the last 30 days (lifetime to ${today.toLocaleDateString()})`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Arithmetic Operations</H3>

                    <p>
                        You can now perform arithmetic operations on metrics and numeric properties
                        (like budgets or bids).
                    </p>

                    <p className="mt-1">
                        If you wanted to find items where the bid is less than the average CPC over
                        the past 30 days plus $0.05, then you can do that with the following:
                    </p>

                    <SyntaxHighlightedCode
                        code={`bid < cpc(30d) + $0.05`}
                        version={2}
                        className="mt-4"
                    />

                    <p>Addition, subtraction, multiplication, and division are supported.</p>

                    <p className="mt-1">
                        Multiple arithmetic operators are evaluated via order of operations.
                        Multiplication and division are evaluated first, then addition and
                        subtraction.
                    </p>

                    <SyntaxHighlightedCode
                        code={`bid * 2 + $0.05 < 10`}
                        version={2}
                        className="mt-4"
                    />

                    <p className="mt-1">is equivalent to</p>
                    <SyntaxHighlightedCode
                        code={`(bid * 2) + $0.05 < 10`}
                        version={2}
                        className="mt-4"
                    />
                    <p className="mt-1">
                        but <b>not</b> equivalent to
                    </p>

                    <SyntaxHighlightedCode
                        code={`bid * (2 + $0.05) < 10`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Comments</H3>

                    <p>Comments are supported by starting the line with `//`.</p>

                    <SyntaxHighlightedCode
                        code={`// This is a comment\nbid < cpc(30d) + $0.05`}
                        version={2}
                        className="mt-4"
                    />

                    <p>Or multiline comments.</p>

                    <SyntaxHighlightedCode
                        code={`/*\nThis is a multiline comment\n*/\nbid < cpc(30d) + $0.05`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Grouping</H3>

                    <p>Parentheses are used to group expressions together.</p>

                    <SyntaxHighlightedCode
                        code={`(sales(30d) = 0 and clicks(30d) > 10)
or (acos(90d) > 40% and sales(90d) < $50)`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Operator Precedence</H3>
                    <p>
                        You should be aware of operator precedence. V1 syntax did not allow you to
                        mix AND and OR in the same expressions.
                    </p>
                    <p className="mt-1">
                        You could <b>not</b> do something like
                    </p>
                    <SyntaxHighlightedCode
                        code={`over the last 30 days
   clicks > 10 and impressions > 100 or sales = 0`}
                        version={1}
                        className="mt-4"
                    />
                    <p className="mt-1">
                        It would error. You had to be explicit about where the parentheses were
                        placed. So either
                    </p>
                    <SyntaxHighlightedCode
                        code={`over the last 30 days
   (clicks > 10 and impressions > 100) or sales = 0`}
                        version={1}
                        className="mt-4"
                    />
                    or
                    <SyntaxHighlightedCode
                        code={`over the last 30 days
   clicks > 10 and (impressions > 100 or sales = 0)`}
                        version={1}
                        className="mt-4"
                    />
                    <p className="mt-1">
                        The v2 syntax does not have this restriction. You can mix AND and OR freely.
                        It relies on something call operator precedence, which is basically just a
                        fancy way of saying that the "AND" items are evaluated first.
                    </p>
                    <p className="mt-1">`AND` has a higher precedence than `OR`.</p>
                    <p className="mt-1">So this:</p>
                    <SyntaxHighlightedCode
                        code={`clicks(30d) > 10 and impressions(30d) > 100 or sales(30d) = 0`}
                        version={2}
                        className="mt-4"
                    />
                    <p className="mt-1">is equivalent to this:</p>
                    <SyntaxHighlightedCode
                        code={`(clicks(30d) > 10 and impressions(30d) > 100) or sales(30d) = 0`}
                        version={2}
                        className="mt-4"
                    />
                    <p className="mt-1">but this would give different results:</p>
                    <SyntaxHighlightedCode
                        code={`clicks(30d) > 10 and (impressions(30d) > 100 or sales(30d) = 0)`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">String Operations</H3>

                    <p>
                        Properties that contain text (like the campaign name or ad group name) can
                        be compared using several operators:
                    </p>

                    <ul className="list-disc pl-6">
                        <li>`=` exact match</li>
                        <li>`!=` not equal</li>
                        <li>`contains` substring match</li>
                        <li>`does not contain` substring non-match</li>
                        <li>`starts with` prefix match</li>
                        <li>`ends with` suffix match</li>
                    </ul>

                    <p className="mt-1">For example:</p>
                    <SyntaxHighlightedCode
                        code={`campaign name contains "lottery"`}
                        version={2}
                        className="mt-4"
                    />
                    <SyntaxHighlightedCode
                        code={`campaign name start with "B00KSB00KS"`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Reference</H3>

                    <p>A full list of all currently supported metrics and properties is below.</p>

                    <div className="w-full flex">
                        <div className="w-1/2">
                            <p className="mt-4">
                                <b>Standard Metrics</b>
                            </p>
                            <ul className="list-disc pl-6">
                                <li>clicks</li>
                                <li>impressions</li>
                                <li>sales</li>
                                <li>acos</li>
                                <li>cost</li>
                                <li>spend</li>
                                <li>orders</li>
                                <li>cpc</li>
                                <li>ctr</li>
                                <li>cvr</li>
                                <li>roas</li>
                                <li>rpc</li>
                                <li>cac</li>
                                <li>aov</li>
                            </ul>

                            <p className="mt-4">
                                <b>KDP only Metrics</b>
                            </p>
                            <ul className="list-disc pl-6">
                                <li>pages read</li>
                                <li>estimated royalties</li>
                                <li>adjusted sales</li>
                                <li>adjusted orders</li>
                                <li>adjusted pages read</li>
                                <li>adjusted estimated royalties</li>
                                <li>blended profit</li>
                                <li>blended rpc</li>
                                <li>blended acos</li>
                                <li>blended roas</li>
                                <li>blended cvr</li>
                                <li>blended cac</li>
                                <li>blended aov</li>
                            </ul>
                        </div>
                        <div className="w-1/2">
                            <p className="mt-4">
                                <b>Properties</b>
                            </p>
                            <ul className="list-disc pl-6">
                                <li>state</li>
                                <li>bid</li>
                                <li>campaign name</li>
                                <li>ad group name</li>
                                <li>budget</li>
                                <li>daily budget</li>
                                <li>default bid</li>
                                <li>campaign start date</li>
                                <li>campaign end date</li>
                            </ul>
                        </div>
                    </div>
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">Variables</H3>

                    <p>
                        Variables are a new feature in the v2 syntax. They allow you to store a
                        value and reuse it in your recipe.
                    </p>

                    <p className="mt-1">
                        Variables are declared using the `let` keyword. They need to be declared at
                        the top of your recipe, before you use them. They also must end with a
                        semicolon.
                    </p>

                    <SyntaxHighlightedCode
                        code={`let recent_sales = sales(30d);\nrecent_sales > $1000`}
                        version={2}
                        className="mt-4"
                    />

                    <p className="mt-1">Variables can contain math and other expressions.</p>

                    <SyntaxHighlightedCode
                        code={`// Find items that have a sales drop of more than 20%\nlet change_in_sales = sales(30d) / sales(31d..61d);\nlet drop_threshold = 20%;\nchange_in_sales < (1 - drop_threshold/100)`}
                        version={2}
                        className="mt-4"
                    />
                </Card>

                <Card className="mb-6 p-6">
                    <H3 className="mb-4">If Statements</H3>

                    <p>
                        If statements are a new feature in the v2 syntax. They allow you to
                        conditionally declare values.
                    </p>

                    <SyntaxHighlightedCode
                        code={`let sales_volume = case(\n    when sales(30d) > $1000 then "high",\n    when sales(30d) > $500 then "medium",\n    else "low"\n);\n\n(sales_volume = "high" and acos(30d) > 30%)\nor\n(sales_volume = "medium" and acos(30d) > 40%)`}
                        version={2}
                        className="mt-4"
                    />

                    <p className="mt-1">
                        The `case` function is used to declare the value. It takes a list of
                        conditions and a default value.
                    </p>

                    <p className="mt-1">
                        You can have any number of conditionals within a case statement. All case
                        statements need to have at least one "when... then" condition and an "else"
                        condition.
                    </p>
                </Card>
            </div>
        </Layout>
    )
}

export default RecipesV2Quickstart
